import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Pattern for partner labels: PARTNER-001, ACME-042, etc.
const PARTNER_LABEL_PATTERN = /^([A-Z0-9_-]+)-(\d{3,})$/;

// Pattern for user-specific prepaid labels: ES-XXXXXXXX
const USER_PREPAID_PATTERN = /^ES-[A-Z0-9]{8}$/;

export default function QRCodeRouter() {
  const { boxNumber } = useParams<{ boxNumber: string }>();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuthAndRoute = async () => {
      if (!boxNumber) {
        navigate('/');
        return;
      }

      // Get current session FIRST
      const { data: { session } } = await supabase.auth.getSession();

      // Look up if this box exists and is claimed
      const { data: box } = await supabase
        .from('boxes')
        .select('id, user_id, location_status')
        .eq('box_number', boxNumber)
        .maybeSingle();

      // Check if this is a user-specific prepaid box (ES-XXXXXXXX format)
      const isUserPrepaidBox = USER_PREPAID_PATTERN.test(boxNumber);

      // Box exists - handle routing based on ownership and status
      if (box) {
        // User not logged in
        if (!session?.user) {
          if (isUserPrepaidBox) {
            // Prepaid box - need to log in to access
            // Store intended destination and redirect to auth
            navigate(`/auth?redirect=/qr/${boxNumber}`);
            return;
          }
          // Regular box - go to lead capture
          navigate(`/try?boxNumber=${boxNumber}`);
          return;
        }

        // User is logged in - check if admin
        const { data: isAdmin } = await supabase.rpc('has_role', {
          _user_id: session.user.id,
          _role: 'admin'
        });

        if (isAdmin) {
          navigate(`/admin/facility-scanner?boxNumber=${boxNumber}`);
          return;
        }

        // Check if user owns the box
        if (box.user_id === session.user.id) {
          // For prepaid boxes with pending_user_setup, go to box setup
          if (box.location_status === 'pending_user_setup') {
            navigate(`/box/${box.id}?setup=true`);
            return;
          }
          // Regular owned box - go to detail page
          navigate(`/box/${box.id}`);
          return;
        }

        // User logged in but doesn't own this box
        if (isUserPrepaidBox) {
          // This prepaid box belongs to someone else
          toast.error("This label belongs to another account");
          navigate('/dashboard');
          return;
        }

        // Regular box that user doesn't own
        navigate('/dashboard');
        return;
      }

      // Box doesn't exist in database
      if (isUserPrepaidBox) {
        // Prepaid box code not found - this shouldn't happen
        toast.error("Invalid QR code - box not found");
        navigate('/');
        return;
      }

      // Box doesn't exist or is unclaimed (partner labels, new boxes)
      // NOW check for partner label format for attribution
      const partnerMatch = boxNumber.match(PARTNER_LABEL_PATTERN);
      
      if (session?.user) {
        // Logged in user can claim this box
        navigate(`/box/new?boxNumber=${boxNumber}`);
      } else if (partnerMatch) {
        // Not logged in + partner label = lead capture with attribution
        const partnerCode = partnerMatch[1];
        navigate(`/try?boxNumber=${boxNumber}&source=${partnerCode}`);
      } else {
        // Not logged in + no partner = generic lead capture
        navigate(`/try?boxNumber=${boxNumber}`);
      }
    };

    checkAuthAndRoute();
  }, [boxNumber, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span>Redirecting...</span>
      </div>
    </div>
  );
}
